import {
  Slider as AriaSlider,
  SliderOutput,
  SliderThumb,
  SliderTrack,
  Label,
  type SliderProps as AriaSliderProps,
} from 'react-aria-components'
import { cn } from '@/lib/utils'

export interface SliderProps extends AriaSliderProps {
  label?: string
  showValue?: boolean
  formatValue?: (value: number) => string
}

export function Slider({
  className,
  label,
  showValue = false,
  formatValue,
  'aria-label': ariaLabel,
  ...props
}: SliderProps) {
  // Ensure we always have an accessible label
  const accessibleLabel = label || ariaLabel || 'Slider'

  return (
    <AriaSlider
      className={cn('flex flex-col gap-2 w-full', className)}
      aria-label={!label ? accessibleLabel : undefined}
      {...props}
    >
      {(label || showValue) && (
        <div className="flex items-center justify-between">
          {label && (
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {label}
            </Label>
          )}
          {showValue && (
            <SliderOutput className="text-sm text-gray-600 dark:text-gray-400">
              {({ state }) =>
                formatValue
                  ? formatValue(state.values[0])
                  : state.values[0].toString()
              }
            </SliderOutput>
          )}
        </div>
      )}
      <SliderTrack className="group relative h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
        {({ state }) => (
          <>
            {/* Filled track */}
            <div
              className="absolute h-full rounded-full bg-brand-500"
              style={{ width: `${state.getThumbPercent(0) * 100}%` }}
            />
            <SliderThumb
              index={0}
              aria-label={`${accessibleLabel} thumb`}
              className={cn(
                'top-1/2 h-4 w-4 rounded-full bg-white border-2 border-brand-500 shadow',
                'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
                'cursor-grab active:cursor-grabbing',
                'transition-shadow',
                'dragging:cursor-grabbing'
              )}
              style={{
                transform: 'translate(-50%, -50%)',
              }}
            />
          </>
        )}
      </SliderTrack>
    </AriaSlider>
  )
}

export default Slider
